import create from 'zustand';
import { persist } from 'zustand/middleware';

export const PREDEFINED_ROLE_PERMISSIONS = {
  Admin: [
    'viewDashboard',
    'assignRole',
    'viewEmployee',
    'viewUserStatus',
    'viewUsers',
    'userAction',
    'TDSRegistrationDetails',
    'TDSCredentialsDetails',
    'TDSDetails',
    'viewPaymentStatus',
    'screenPermission',
    'updateProfile',
    'ReturnOtp',
    'uploadBanner',
    'logOut',
  ],
  'Registration team': [
    'viewUserStatus',
    'viewUsers',
    'TDSRegistrationDetails',
    'TDSCredentialsDetails',
    'TDSDetails',
    'screenPermission',
    'updateProfile',
  ],
  'ITR filing team': [
    'TDSCredentialsDetails',
    'viewPaymentStatus',
    'ReturnOtp',
    'updateProfile',
    'screenPermission',
  ],
  'Marketing team': ['updateProfile', 'uploadBanner'],
};

/**
 * Normalize employee permissions from Firestore / localStorage into a string[].
 */
export function normalizePermissions(raw) {
  if (!raw) return [];

  if (Array.isArray(raw)) {
    return raw
      .map((p) => {
        if (typeof p === 'string') return p.trim();
        if (p && typeof p === 'object') {
          // Firestore sometimes stores weird shapes
          const value = p.id || p.name || p.permission || p.key;
          return typeof value === 'string' ? value.trim() : '';
        }
        return String(p || '').trim();
      })
      .filter(Boolean);
  }

  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (typeof parsed !== 'string' || parsed !== trimmed) {
        return normalizePermissions(parsed);
      }
    } catch {
      // not JSON
    }
    return trimmed
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean);
  }

  if (typeof raw === 'object') {
    return Object.entries(raw)
      .filter(([, enabled]) => Boolean(enabled))
      .map(([key]) => key);
  }

  return [];
}

export function resolvePermissions(permissions, role) {
  const normalized = normalizePermissions(permissions);
  if (normalized.length) return normalized;
  if (role && PREDEFINED_ROLE_PERMISSIONS[role]) {
    return [...PREDEFINED_ROLE_PERMISSIONS[role]];
  }
  return [];
}

export const useAuthStore = create(
  persist(
    (set, get) => ({
      role: null,
      permissions: [],
      user: null,
      setSession: ({ role, permissions, user }) => {
        const resolved = resolvePermissions(permissions, role);
        set({
          role: role || null,
          permissions: resolved,
          user: user || null,
        });
        // Keep legacy keys in sync for older code paths
        try {
          if (role) localStorage.setItem('userRole', role);
          localStorage.setItem('userPermissions', JSON.stringify(resolved));
          if (user) localStorage.setItem('user', JSON.stringify(user));
        } catch {
          // ignore storage errors
        }
      },
      clearSession: () => {
        set({ role: null, permissions: [], user: null });
        try {
          localStorage.removeItem('userRole');
          localStorage.removeItem('userPermissions');
          localStorage.removeItem('user');
        } catch {
          // ignore
        }
      },
      getPermissions: () => {
        const state = get();
        return resolvePermissions(state.permissions, state.role);
      },
    }),
    {
      name: 'moneycart-auth',
      version: 1,
      partialize: (state) => ({
        role: state.role,
        permissions: state.permissions,
        user: state.user,
      }),
    }
  )
);

/** Sync App state helpers from legacy localStorage if zustand is empty */
export function hydrateAuthFromLegacyStorage() {
  const state = useAuthStore.getState();
  if (state.permissions?.length || state.role) return;

  try {
    const role = localStorage.getItem('userRole');
    const rawPerms = localStorage.getItem('userPermissions');
    const rawUser = localStorage.getItem('user');
    let permissions = [];
    let user = null;
    if (rawPerms) {
      try {
        permissions = JSON.parse(rawPerms);
      } catch {
        permissions = rawPerms;
      }
    }
    if (rawUser) {
      try {
        user = JSON.parse(rawUser);
      } catch {
        user = null;
      }
    }
    if (role || permissions) {
      useAuthStore.getState().setSession({ role, permissions, user });
    }
  } catch {
    // ignore
  }
}
